import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Role } from '@gripp/shared-logic';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { CheckBoxSelector } from '../filter/checkBoxSelector';
import { DropDownView } from '../modals/dropDownView';

type PermittedRolesSelectorProps = {
  roles: Role[];
  value?: string[] | null;
  onChange: (value: string[] | null) => void;
};

export const PermittedRolesSelector = ({
  roles,
  value,
  onChange,
}: PermittedRolesSelectorProps) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);
  const anchorRef = useRef<View>(null);

  const workspaceAdminRole = useMemo(
    () => roles.find((r) => r.name === 'Workspace Admin'),
    [roles]
  );

  const selectableRoles = useMemo(
    () => roles.filter((r) => r.name !== 'Global Admin'),
    [roles]
  );

  // Convert null/empty to all roles selected for display
  const selectedRoleIds = useMemo(() => {
    if (!value || value.length === 0) {
      return selectableRoles.map((role) => role.id);
    }
    return value;
  }, [value, selectableRoles]);

  const handleToggleRole = useCallback(
    (roleId: string) => {
      let newSelectedRoles: string[];

      if (selectedRoleIds.includes(roleId)) {
        newSelectedRoles = selectedRoleIds.filter((id) => id !== roleId);
      } else {
        newSelectedRoles = [...selectedRoleIds, roleId];
      }

      // Always include Workspace Admin
      if (
        workspaceAdminRole &&
        !newSelectedRoles.includes(workspaceAdminRole.id)
      ) {
        newSelectedRoles = [...newSelectedRoles, workspaceAdminRole.id];
      }

      // If all roles are selected, save as null (accessible to all)
      if (newSelectedRoles.length === selectableRoles.length) {
        onChange(null);
      } else {
        onChange(newSelectedRoles);
      }
    },
    [selectedRoleIds, onChange, workspaceAdminRole, selectableRoles]
  );

  return (
    <View style={styles.container}>
      <Pressable
        ref={anchorRef}
        onPress={() => setIsOpened(!isOpened)}
        style={styles.button}
      >
        <Text style={styles.buttonText}>
          {t('routine.permittedRoles.label')}
        </Text>
        <MaterialCommunityIcons
          name="chevron-down"
          size={16}
          color={Colors.role.textStrong}
        />
      </Pressable>

      <DropDownView
        isVisible={isOpened}
        onDismiss={() => setIsOpened(false)}
        anchorRef={anchorRef}
      >
        <View style={styles.dropdownContent}>
          <Text style={styles.dropdownTitle}>
            {t('routine.permittedRoles.label')}
          </Text>
          {selectableRoles.map((role) => (
            <View key={role.id} style={styles.checkboxRow}>
              <CheckBoxSelector
                option={role.id}
                options={selectedRoleIds}
                onChange={handleToggleRole}
                label={role.name}
                disabled={role.name === 'Workspace Admin'}
              />
            </View>
          ))}
        </View>
      </DropDownView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginRight: 8,
  },
  button: {
    height: 32,
    borderRadius: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    backgroundColor: Colors.role.surface,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    marginHorizontal: 4,
    color: Colors.role.textStrong,
  },
  dropdownContent: {
    padding: 16,
  },
  dropdownTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
    color: Colors.role.textPrimary,
  },
  checkboxRow: {
    marginBottom: 8,
  },
});
